/**
 * RankedSourceList — typed renderer for the `source_list` payload
 * (`find_document`).
 *
 * One card per document: title, locator, why it is relevant, and an
 * open-in-viewer action wired to the existing citation/document viewer.
 *
 * Documents are rendered in payload order — the engine ranks them.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { PiArrowSquareOutLight, PiFileTextLight } from 'react-icons/pi';
import GlassCard from '../../shared/brand/GlassCard';
import Button from '../../shared/TailwindComponents/Button';
import {
  ResearchCitation,
  SourceListItem,
  SourceListPayload,
  citationHighlight,
  isUploadedDocument,
} from '../types';
import { CitationHandlers, UploadedFileBadge, findCitation, openCitation } from './CitationChip';

interface Props extends CitationHandlers {
  payload: SourceListPayload;
  citations: ResearchCitation[];
}

/**
 * Resolve the citation behind a listed document so opening it reuses the
 * citation's provenance (and its highlight range when the offsets are
 * chunker-grade). Falls back to a citation synthesised from the item.
 */
function citationFor(item: SourceListItem, citations: ResearchCitation[]): ResearchCitation {
  const byNumber = typeof item.number === 'number' ? findCitation(citations, item.number) : undefined;
  if (byNumber) return byNumber;
  const byDocument = item.document_uuid
    ? citations.find((c) => c.document_uuid === item.document_uuid)
    : undefined;
  if (byDocument) return byDocument;

  return {
    id: item.document_uuid || '',
    number: item.number ?? 0,
    title: item.title || item.file_name || '',
    snippet: item.snippet || '',
    section: item.locator || '',
    law: '',
    paragraph: '',
    url: item.url || '',
    provider: item.provider || '',
    source_type: item.source_type || '',
    relevance_tier: item.relevance_tier || '',
    score: item.score ?? null,
    start_char: null,
    end_char: null,
    // No offsets on a synthesised citation — never claim a passage we lack.
    offset_source: 'unavailable',
    file_name: item.file_name || '',
    document_uuid: item.document_uuid || '',
    // The path is what makes an item openable — accept every spelling the
    // engine has used, or a click resolves to nothing to fetch.
    blob_path:
      (item as any).blob_path || (item as any).path || (item as any).source_path || '',
    marker: (item as any).marker || `[${item.number ?? 0}]`,
    document_type: (item as any).document_type || '',
    authority: (item as any).authority || '',
    jurisdiction: (item as any).jurisdiction || '',
    date: (item as any).date || '',
    document_number: (item as any).document_number || '',
    pinpoint: (item as any).pinpoint || item.locator || '',
    quote: (item as any).quote || '',
    quote_start: null,
    quote_end: null,
    quote_exact: false,
    asserts: (item as any).asserts || (item as any).supports || '',
    asserts_kind: 'model_generated',
    supports: (item as any).supports || (item as any).asserts || '',
    justification: (item as any).justification || '',
    container: (item as any).container || '',
    locator: null,
    index_name: (item as any).index_name || '',
  };
}

const RankedSourceList: React.FC<Props> = ({ payload, citations, ...handlers }) => {
  const { t } = useTranslation();
  const documents = Array.isArray(payload.documents) ? payload.documents : [];

  return (
    <div className="font-sans space-y-3 animate-fade-in" data-testid="ranked-source-list">
      {payload.title && (
        <h3 className="text-sm font-semibold text-fg-strong">{payload.title}</h3>
      )}
      {payload.summary && (
        <p className="text-sm leading-relaxed text-fg-body">{payload.summary}</p>
      )}
      {typeof payload.total_found === 'number' && (
        <p className="text-[11px] text-fg-muted">
          {t('Documents found')}: {payload.total_found}
        </p>
      )}

      <div className="space-y-2">
        {documents.map((item, i) => {
          const citation = citationFor(item, citations);
          const uploaded = isUploadedDocument(citation) || item.provider === 'uploaded_document';
          const canHighlight = !!citationHighlight(citation);
          const locator = item.locator || citation.section;

          return (
            <GlassCard
              key={item.document_uuid || `${i}`}
              padding="sm"
              data-testid="source-card"
              className="reveal-item transition-shadow duration-200 ease-slide hover:shadow-md"
              style={{ '--reveal-delay': `${Math.min(i, 10) * 50}ms` } as React.CSSProperties}
            >
              <div className="flex items-start gap-3">
                <PiFileTextLight
                  className="w-4 h-4 mt-0.5 text-fg-muted flex-shrink-0"
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-fg-strong truncate">
                      {item.title || item.file_name || `${t('Document')} ${i + 1}`}
                    </p>
                    {uploaded && <UploadedFileBadge fileName={item.file_name} />}
                  </div>
                  {locator && (
                    <p className="mt-0.5 text-[11px] font-medium text-fg-muted">{locator}</p>
                  )}
                  {/* The DOCUMENT first, in a quotation treatment. `quote` is
                      the verbatim span the engine located; `snippet` is the
                      merged excerpt and is the fallback. Order and typography
                      both matter: `why_relevant` used to sit ABOVE this in
                      identical type, so the model's summary read as the
                      document's opening line (BUG-01, narrower vector). */}
                  {(citation.quote || item.snippet) && (
                    <blockquote
                      className="mt-1 border-l-2 border-hairline-soft pl-2 font-serif
                                 text-xs leading-relaxed text-fg-body line-clamp-3"
                      data-testid="source-quote"
                    >
                      &ldquo;{citation.quote || item.snippet}&rdquo;
                    </blockquote>
                  )}
                  {/* The MODEL's summary, below and labelled as such. */}
                  {item.why_relevant && (
                    <p className="mt-1 text-xs leading-relaxed text-fg-muted"
                       data-testid="source-why-relevant">
                      <span className="font-medium">{t('Why this is relevant')}:</span>{' '}
                      {item.why_relevant}
                    </p>
                  )}
                </div>
                <Button
                  variant="tertiary"
                  size="small"
                  icon={<PiArrowSquareOutLight className="w-3.5 h-3.5" />}
                  ariaLabel={t('Open document')}
                  onClick={() => openCitation(citation, handlers)}
                  data-testid="source-open"
                >
                  {canHighlight ? t('Open passage') : t('Open')}
                </Button>
              </div>
            </GlassCard>
          );
        })}
      </div>
    </div>
  );
};

export default RankedSourceList;
